import { AccountVariableType, ItContactValue } from '@/client/accounts';
import { AppAccordion } from '@/ui/accordion/AppAccordion';
import { FlexContainer } from '@/ui/styled-ui';
import classNames from 'classnames';
import { AccordionTab } from 'primereact/accordion';
import { InputText } from 'primereact/inputtext';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledCardAccordion = styled(AppAccordion)`
  &.p-accordion {
    .p-accordion-tab {
      padding: 0;
      background-color: var(--white-main);
      border: 1px solid var(--gray-dark);
      border-radius: var(--box-medium-border-radius);
      overflow: hidden;

      .p-accordion-header .p-accordion-header-link {
        background-color: var(--beige-main);
        border: none;
        border-radius: 0;
        padding: var(--medium-padding) var(--default-padding);
      }

      .p-accordion-header .p-accordion-header-link:not(.p-disabled):hover,
      &:not(.p-disabled).p-highlight .p-accordion-header-link,
      &:not(.p-disabled).p-highlight:hover .p-accordion-header-link {
        background-color: var(--beige-main);
      }

      .p-accordion-toggle-icon {
        color: var(--black-main);
        padding-left: var(--medium-padding);
      }

      .p-accordion-content {
        background-color: var(--white-main);
        padding: var(--default-padding);
        border-top: 1px solid var(--gray-dark);
      }
    }
  }
`;

const StyledTitle = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: var(--black-main);
  white-space: nowrap;
`;

const StyledCodePill = styled.code`
  font-family: monospace;
  font-size: 11px;
  color: var(--black-main);
  background: var(--platinum-main);
  border-radius: var(--xxxsmall-border-radius);
  padding: var(--xxxsmall-padding);
`;

const StyledStatusPill = styled.span<{ $filled: boolean }>`
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  white-space: nowrap;
  padding: var(--xxxsmall-padding) var(--xsmall-padding);
  border-radius: var(--grand-border-radius);
  background: ${({ $filled }) =>
    $filled ? 'var(--green-pale)' : 'var(--platinum-main)'};
  color: ${({ $filled }) =>
    $filled ? 'var(--green-status)' : 'var(--gray-darkest)'};
`;

const StyledFormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--medium-big-padding);
  width: 100%;

  .p-inputtext {
    width: 100%;
    background: var(--beige-main);
    border: none;
    border-radius: var(--default-border-radius);
    min-height: 50px;
    padding: var(--small-padding) var(--medium-padding);
    color: var(--black-main);

    &::placeholder {
      color: var(--gray-medium-dark);
    }
  }
`;

const StyledRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--default-padding);
  width: 100%;
`;

const StyledFieldColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--semi-xsmall-padding);
  flex: 1 1 240px;
  min-width: 0;
`;

const StyledFieldFull = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--semi-xsmall-padding);
  width: 100%;
`;

const StyledLabel = styled.label`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--black-main);
`;

const StyledRequired = styled.span`
  color: var(--red-main);
`;

const StyledHelper = styled.small`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--gray-darker);
`;

const StyledError = styled.small`
  font-size: var(--xsmall-font-size);
  font-weight: 500;
  color: var(--red-main);
`;

const StyledPreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--xsmall-padding);
  width: 100%;
`;

const StyledPreviewLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--gray-medium-dark);
`;

const StyledPreviewBox = styled.div`
  width: 100%;
  background: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--box-border-radius);
  padding: var(--default-padding-small);
  font-size: var(--small-font-size);
  line-height: 1.6;
  color: var(--black-main);
`;

const StyledPreviewName = styled.span`
  font-weight: 700;
  text-decoration: underline;
`;

type ItContactVariableCardProps = {
  value: ItContactValue;
  onChange: (value: ItContactValue) => void;
  nameError?: string;
  isFilled: boolean;
};

export const ItContactVariableCard: React.FC<ItContactVariableCardProps> = ({
  value,
  onChange,
  nameError,
  isFilled,
}) => {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState<number | number[] | null>(0);

  return (
    <StyledCardAccordion
      activeIndex={activeIndex}
      onTabChange={(e) => setActiveIndex(e.index)}
    >
      <AccordionTab
        headerTemplate={
          <FlexContainer align="center" justify="flex-start" gap={10}>
            <StyledTitle>
              {t('account.trainingSettings.variables.itContact.title')}
            </StyledTitle>
            <StyledCodePill>{`{{${AccountVariableType.IT_CONTACT}}}`}</StyledCodePill>
            <StyledStatusPill $filled={isFilled}>
              {isFilled
                ? t('account.trainingSettings.variables.status.filled')
                : t('account.trainingSettings.variables.status.notFilled')}
            </StyledStatusPill>
          </FlexContainer>
        }
      >
        <StyledFormBody>
          <StyledRow>
            <StyledFieldColumn>
              <StyledLabel htmlFor="it-contact-name">
                {t('account.trainingSettings.variables.name.label')}
                <StyledRequired> *</StyledRequired>
              </StyledLabel>
              <InputText
                id="it-contact-name"
                className={classNames('w-full', { 'p-invalid': !!nameError })}
                value={value.name}
                placeholder={t(
                  'account.trainingSettings.variables.name.placeholder',
                )}
                onChange={(e) => onChange({ ...value, name: e.target.value })}
              />
              {nameError ? (
                <StyledError>{nameError}</StyledError>
              ) : (
                <StyledHelper>
                  {t('account.trainingSettings.variables.name.helper')}
                </StyledHelper>
              )}
            </StyledFieldColumn>

            <StyledFieldColumn>
              <StyledLabel htmlFor="it-contact-contact">
                {t('account.trainingSettings.variables.contact.label')}
              </StyledLabel>
              <InputText
                id="it-contact-contact"
                className="w-full"
                value={value.contact ?? ''}
                placeholder={t(
                  'account.trainingSettings.variables.contact.placeholder',
                )}
                onChange={(e) =>
                  onChange({ ...value, contact: e.target.value })
                }
              />
              <StyledHelper>
                {t('account.trainingSettings.variables.contact.helper')}
              </StyledHelper>
            </StyledFieldColumn>
          </StyledRow>

          <StyledFieldFull>
            <StyledLabel htmlFor="it-contact-other">
              {t('account.trainingSettings.variables.other.label')}
            </StyledLabel>
            <InputText
              id="it-contact-other"
              className="w-full"
              value={value.other ?? ''}
              placeholder={t(
                'account.trainingSettings.variables.other.placeholder',
              )}
              onChange={(e) => onChange({ ...value, other: e.target.value })}
            />
            <StyledHelper>
              {t('account.trainingSettings.variables.other.helper')}
            </StyledHelper>
          </StyledFieldFull>

          <StyledPreviewSection>
            <StyledPreviewLabel>
              {t('account.trainingSettings.variables.preview.label')}
            </StyledPreviewLabel>
            <StyledPreviewBox>
              <Trans
                i18nKey="account.trainingSettings.variables.preview.template"
                values={{
                  name:
                    (value.name && value.name.trim()) ||
                    t('account.trainingSettings.variables.name.placeholder'),
                }}
                components={{ name: <StyledPreviewName /> }}
              />
            </StyledPreviewBox>
          </StyledPreviewSection>
        </StyledFormBody>
      </AccordionTab>
    </StyledCardAccordion>
  );
};
